import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, usePortalMe, useSubmitApplication } from "../portal/api.js";
import {
  emptyProfileForm,
  ProfileFormFields,
  type ProfileFormState,
} from "../portal/ProfileFormFields.js";
import { cityLabelsForTownKeys, zipsForTownKeys } from "../serviceArea/qcServiceArea.js";

/**
 * In-portal application (unit 16, the no-match path): a signed-up
 * contractor with no registry record applies here, creating a
 * PENDING_REVIEW record linked to their account. Structured categories and
 * service areas are collected up front so approval needs no triage.
 */
export function PortalApply() {
  const navigate = useNavigate();
  const { data: me } = usePortalMe();
  const submit = useSubmitApplication();

  const [form, setForm] = useState<ProfileFormState | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Prefill the business email with the login email once /portal/me loads.
  const state = form ?? { ...emptyProfileForm(), email: me?.email ?? "" };
  const onChange = (patch: Partial<ProfileFormState>) => setForm({ ...state, ...patch });

  // Already linked (e.g. back-navigation after applying): nothing to do here.
  if (me?.contractor) {
    return (
      <div className="mx-auto max-w-md px-6 py-10">
        <p className="text-base text-charcoal">
          Your account is already linked to <strong>{me.contractor.businessName}</strong>.
        </p>
        <Link to="/portal" className="mt-4 inline-block font-semibold text-brand-700">
          Back to opportunities
        </Link>
      </div>
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (state.serviceCategories.length === 0) {
      setError("Pick at least one service you offer.");
      return;
    }
    if (state.selectedTowns.length === 0) {
      setError("Select the towns you serve on the map.");
      return;
    }

    try {
      await submit.mutateAsync({
        businessName: state.businessName.trim(),
        contactName: state.contactName.trim(),
        email: state.email.trim(),
        phone: state.phone.trim(),
        ...(state.website.trim() ? { website: state.website.trim() } : {}),
        serviceCategories: state.serviceCategories,
        serviceArea: {
          postalCodes: zipsForTownKeys(state.selectedTowns),
          cities: cityLabelsForTownKeys(state.selectedTowns),
        },
        yearsExperience: Number(state.yearsExperience || "0"),
        ...(state.licenseInfo.trim() ? { licenseInfo: state.licenseInfo.trim() } : {}),
        ...(state.insuranceInfo.trim() ? { insuranceInfo: state.insuranceInfo.trim() } : {}),
        ...(state.about.trim() ? { about: state.about.trim() } : {}),
      });
      navigate("/portal", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          "A pro profile already exists for that business email. If it's yours, sign in with that email address instead and it will link automatically.",
        );
      } else {
        setError(err instanceof Error ? err.message : "Could not submit your application");
      }
    }
  }

  return (
    <div className="mx-auto max-w-md px-6 py-10">
      <h1 className="text-xl font-bold text-charcoal">Apply to get matched</h1>
      <p className="mt-2 text-sm text-charcoal">
        Tell us about your business. We review every pro before sending job invitations —
        you&rsquo;ll hear from us by email.
      </p>

      <form className="mt-8 space-y-5" onSubmit={(e) => void onSubmit(e)} noValidate>
        <ProfileFormFields state={state} onChange={onChange} showIdentity />

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submit.isPending}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-brand-700 px-6 text-base font-semibold text-white shadow-sm active:bg-brand-800 disabled:opacity-60"
        >
          {submit.isPending ? "Submitting…" : "Submit application"}
        </button>
      </form>
    </div>
  );
}
